/**
 * Валидатор конфигурации Firebase
 * Проверяет наличие и валидность всех необходимых переменных окружения Firebase
 */

/**
 * Список всех необходимых переменных окружения Firebase
 */
const REQUIRED_ENV_KEYS = {
  REACT_APP_FIREBASE_API_KEY: {
    critical: true,
    description: 'API Key для аутентификации в Firebase',
    location:
      'Project Settings > General > Your apps > SDK setup and configuration',
  },
  REACT_APP_FIREBASE_AUTH_DOMAIN: {
    critical: true,
    description: 'Домен аутентификации Firebase',
    location:
      'Project Settings > General > Your apps > SDK setup and configuration',
  },
  REACT_APP_FIREBASE_PROJECT_ID: {
    critical: true,
    description: 'ID проекта Firebase',
    location:
      'Project Settings > General > Your apps > SDK setup and configuration',
  },
  REACT_APP_FIREBASE_STORAGE_BUCKET: {
    critical: true,
    description: 'Бакет хранилища Firebase',
    location:
      'Project Settings > General > Your apps > SDK setup and configuration',
  },
  REACT_APP_FIREBASE_MESSAGING_SENDER_ID: {
    critical: true,
    description: 'Sender ID для Firebase Cloud Messaging',
    location:
      'Project Settings > General > Your apps > SDK setup and configuration',
  },
  REACT_APP_FIREBASE_APP_ID: {
    critical: true,
    description: 'ID приложения Firebase',
    location:
      'Project Settings > General > Your apps > SDK setup and configuration',
  },
  REACT_APP_FIREBASE_MEASUREMENT_ID: {
    critical: false,
    description: 'ID для Google Analytics (опционально)',
    location:
      'Project Settings > General > Your apps > SDK setup and configuration',
  },
};

/**
 * Проверяет наличие и валидность конфигурации Firebase
 * @returns {Object} Результат валидации с полями:
 *   - valid: boolean - общая валидность конфигурации
 *   - missingCritical: string[] - список отсутствующих критических переменных
 *   - missingOptional: string[] - список отсутствующих опциональных переменных
 *   - emptyValues: string[] - список переменных с пустыми значениями
 */
export function validateFirebaseConfig() {
  console.group('🔥 Firebase Configuration Validation');

  const result = {
    valid: true,
    missingCritical: [],
    missingOptional: [],
    emptyValues: [],
  };

  let hasCriticalIssues = false;
  let hasWarnings = false;

  // Проверяем каждую переменную окружения
  for (const [envKey, config] of Object.entries(REQUIRED_ENV_KEYS)) {
    const value = process.env[envKey];

    // Переменная отсутствует
    if (value === undefined) {
      if (config.critical) {
        result.missingCritical.push(envKey);
        hasCriticalIssues = true;
        console.error(
          `❌ КРИТИЧНО: Отсутствует переменная окружения ${envKey}`
        );
        console.error(`   Описание: ${config.description}`);
        console.error(`   Где найти: Firebase Console > ${config.location}`);
      } else {
        result.missingOptional.push(envKey);
        hasWarnings = true;
        console.warn(
          `⚠️  ПРЕДУПРЕЖДЕНИЕ: Отсутствует переменная окружения ${envKey}`
        );
        console.warn(`   Описание: ${config.description}`);
        console.warn(`   Где найти: Firebase Console > ${config.location}`);
      }
    }
    // Переменная имеет пустое значение
    else if (value.trim() === '') {
      result.emptyValues.push(envKey);
      if (config.critical) {
        hasCriticalIssues = true;
        console.error(
          `❌ КРИТИЧНО: Переменная ${envKey} имеет пустое значение`
        );
      } else {
        hasWarnings = true;
        console.warn(
          `⚠️  ПРЕДУПРЕЖДЕНИЕ: Переменная ${envKey} имеет пустое значение`
        );
      }
    }
  }

  // Если есть критические проблемы, выводим подробную инструкцию
  if (hasCriticalIssues) {
    console.error('\n🚨 КРИТИЧЕСКИЕ ОШИБКИ КОНФИГУРАЦИИ FIREBASE');
    console.error('═════════════════════════════════════════════════════════');
    console.error(
      'Приложение не будет работать корректно без следующей конфигурации:'
    );
    console.error('');
    console.error('📋 Шаги для исправления:');
    console.error('');
    console.error(
      '1. Откройте Firebase Console: https://console.firebase.google.com/'
    );
    console.error('2. Выберите свой проект');
    console.error('3. Перейдите в Project Settings > General');
    console.error('4. Прокрутите вниз до раздела "Your apps"');
    console.error('5. Выберите ваше веб-приложение');
    console.error(
      '6. Скопируйте конфигурацию из раздела "SDK setup and configuration"'
    );
    console.error('');
    console.error('7. Добавьте переменные в файл .env в корне проекта:');
    console.error('   REACT_APP_FIREBASE_API_KEY=your_api_key');
    console.error(
      '   REACT_APP_FIREBASE_AUTH_DOMAIN=your_project.firebaseapp.com'
    );
    console.error('   REACT_APP_FIREBASE_PROJECT_ID=your_project_id');
    console.error(
      '   REACT_APP_FIREBASE_STORAGE_BUCKET=your_project.appspot.com'
    );
    console.error('   REACT_APP_FIREBASE_MESSAGING_SENDER_ID=your_sender_id');
    console.error('   REACT_APP_FIREBASE_APP_ID=your_app_id');
    console.error(
      '   REACT_APP_FIREBASE_MEASUREMENT_ID=your_measurement_id  (опционально)'
    );
    console.error('');
    console.error('8. Перезапустите приложение для применения изменений');
    console.error(
      '═════════════════════════════════════════════════════════\n'
    );
    result.valid = false;
  }

  // Если есть только предупреждения
  if (hasWarnings && !hasCriticalIssues) {
    console.warn('\n⚠️  ПРЕДУПРЕЖДЕНИЯ КОНФИГУРАЦИИ');
    console.warn('Некоторые опциональные переменные отсутствуют.');
    console.warn(
      'Приложение будет работать, но некоторые функции могут быть недоступны.\n'
    );
  }

  // Если всё в порядке
  if (!hasCriticalIssues && !hasWarnings) {
    console.log(
      '\n✅ Конфигурация Firebase валидна! Все необходимые переменные присутствуют.\n'
    );
  }

  console.groupEnd();

  return result;
}

/**
 * Возвращает объект конфигурации Firebase из переменных окружения
 * @throws {Error} Если критические переменные отсутствуют
 * @returns {Object} Объект конфигурации Firebase
 */
export function getFirebaseConfig() {
  const validation = validateFirebaseConfig();

  if (!validation.valid) {
    throw new Error(
      'Firebase configuration is invalid. Please check the console for details.'
    );
  }

  return {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || '',
  };
}
